import { useState, useEffect } from 'react';
import { Button, Input, Label, Badge, Select, SelectItem } from '@fyos/components';
import { X } from 'lucide-react';
import type { Category, Tag, Item, CreateItemData } from '@/lib/itemApi';
import { itemApi } from '@/lib/itemApi';
import type { ColumnDef } from './BoardColumn';
import RichTextEditor from '@/components/RichTextEditor';

export interface CreateItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  columns: ColumnDef[];
  categories: Category[];
  tags: Tag[];
  defaultColumn: string;
  showColumnPicker?: boolean;
  onItemCreated: (item: Item) => void;
}

export default function CreateItemModal({
  open,
  onOpenChange,
  workspaceId,
  columns,
  categories,
  tags,
  defaultColumn,
  showColumnPicker = true,
  onItemCreated,
}: CreateItemModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedColumn, setSelectedColumn] = useState(defaultColumn);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      setSelectedColumn(defaultColumn);
    } else {
      document.body.style.overflow = 'unset';
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setSelectedColumn(defaultColumn);
    setCategoryId(null);
    setSelectedTagIds([]);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const toggleTag = (tagId: string) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    try {
      setCreating(true);
      setError('');
      const data: CreateItemData = {
        title,
        description: description || undefined,
        board_column: selectedColumn,
        category_id: categoryId || undefined,
        tag_ids: selectedTagIds.length > 0 ? selectedTagIds : undefined,
      };
      const item = await itemApi.create(workspaceId, data);
      onItemCreated(item);
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create item');
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div
        className="fixed inset-0 bg-gray-400/50 dark:bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div className="relative z-50 w-full max-w-[768px]" role="dialog" aria-modal="true">
        <div className="relative rounded-2xl bg-white p-6 shadow-xl sm:p-8 dark:bg-gray-800">
          {/* Close button */}
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-white"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Header */}
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Create New Item</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Add a new item to your roadmap board.</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="max-h-[60vh] overflow-y-auto space-y-4">
              <div>
                <Label htmlFor="item-title" required>
                  Title
                </Label>
                <Input
                  id="item-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="What needs to be done?"
                  disabled={creating}
                  autoFocus
                />
              </div>

              <div>
                <Label htmlFor="item-description">Description</Label>
                <RichTextEditor
                  content={description}
                  onChange={setDescription}
                  placeholder="Add more details..."
                  disabled={creating}
                  minHeight="250px"
                />
              </div>

              {showColumnPicker && (
                <div>
                  <Label>Column</Label>
                  <Select
                    value={selectedColumn}
                    onValueChange={setSelectedColumn}
                    disabled={creating}
                    placeholder="Select column..."
                  >
                    {columns.map(col => (
                      <SelectItem key={col.id} value={col.id}>{col.name}</SelectItem>
                    ))}
                  </Select>
                </div>
              )}

              {categories.length > 0 && (
                <div>
                  <Label>Category</Label>
                  <Select
                    value={categoryId || '__none__'}
                    onValueChange={(val) => setCategoryId(val === '__none__' ? null : val)}
                    disabled={creating}
                    placeholder="Select category..."
                  >
                    <SelectItem value="__none__">None</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </Select>
                </div>
              )}

              {tags.length > 0 && (
                <div>
                  <Label>Tags</Label>
                  <div className="flex flex-wrap gap-2">
                    {tags.map(tag => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => toggleTag(tag.id)}
                        disabled={creating}
                      >
                        <Badge
                          variant={selectedTagIds.includes(tag.id) ? 'primary' : 'gray'}
                          size="md"
                          className="cursor-pointer transition-colors"
                        >
                          {tag.name}
                        </Badge>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? 'Creating...' : 'Create Item'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
